import { Button } from 'antd';
import { Modal } from '@app/components/common/Modal/Modal';
import * as S from './ExamMockTestInfo.styles';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ExamMockTestResult } from '../ExamMockTestResult';
import { notificationController } from '@app/controllers/notificationController';
import {
  getMockTestsHis,
  IMockTest,
  IMockTestHis,
  IMockTestUserRequest,
  IMockTestUserStart,
  mockTestCancel,
  mockTestDelete,
  mockTestStart,
} from '@app/api/mockTest.api';
import { MOCK_TEST_TYPE, MOCK_TEST_USER_STATUS } from '@app/constants/mockTest';
import { ExamMockTestTable } from './ExamMockTestTable';
import { useTranslation } from 'react-i18next';
import { getInitialStepAndTime } from '@app/utils/utils';

export const ExamMockTestInfo: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const mockTestId = queryParams.get('id');

  const [isModalResume, setIsModalResume] = useState<boolean>(false);
  const [isModalStart, setIsModalStart] = useState<boolean>(false);
  const [isModalDelete, setIsModalDelete] = useState<boolean>(false);
  const [result, setResult] = useState(false);

  const [data, setData] = useState<IMockTestHis[]>([]);
  const [currentMockTest, setCurrentMockTest] = useState<IMockTestHis>();
  const [detailMockTest, setDetailMockTest] = useState<IMockTest>();
  const [mockTestUserId, setMockTestUserId] = useState<string>('');

  useEffect(() => {
    fetchMockTestHis();
  }, [mockTestId]);

  const fetchMockTestHis = async () => {
    try {
      if (!mockTestId) return;
      const { mockTest, userMockTest } = await getMockTestsHis(mockTestId);
      setData(userMockTest);
      setDetailMockTest(mockTest);
      setCurrentMockTest(userMockTest.length > 0 ? userMockTest[0] : undefined);
    } catch (error) {
      notificationController.error({
        message: error,
      });
    }
  };

  const handleResumeClick = () => {
    const section = detailMockTest?.section ? `&section=${detailMockTest?.section}` : '';
    navigate(`/exam/mock-test/detail?id=${mockTestId}&type=${detailMockTest?.type}${section}`);
  };

  const handleStartClick = () => {
    if (detailMockTest?.type === MOCK_TEST_TYPE.QUESTION_TEST) {
      startMockTest();
    } else {
      const section = detailMockTest?.section ? `&section=${detailMockTest?.section}` : '';
      navigate(`/exam/mock-test/intro?id=${mockTestId}&type=${detailMockTest?.type}${section}`);
    }
  };

  const handleResultClick = (id: string) => {
    navigate(`/result/${id}`);
  };

  const handleModalDeleteClick = (id: string) => {
    setMockTestUserId(id);
    setIsModalDelete(true);
  };

  const handleDeleteClick = async () => {
    try {
      if (!mockTestUserId) return;

      const res = await mockTestDelete(mockTestUserId);
      if (res.statusCode == 200) {
        notificationController.success({ message: `Delete mock test user success` });
        fetchMockTestHis();
      }
    } catch (error) {
      notificationController.error({
        message: error,
      });
    } finally {
      setIsModalDelete(false);
    }
  };

  const handleReset = async () => {
    try {
      if (!mockTestId) return;

      const payload: IMockTestUserRequest = { mockTestId };
      await mockTestCancel(payload);
      setIsModalResume(false);
      fetchMockTestHis();
    } catch (error) {
      notificationController.error({
        message: error,
      });
    }
  };

  const handleResultFinish = (id: string) => {
    setMockTestUserId(id);
    setResult(true);
  };

  const startMockTest = async () => {
    try {
      if (!mockTestId) return;

      const { step, remainTime } = getInitialStepAndTime(detailMockTest?.type || '', detailMockTest?.section || '');

      const payload: IMockTestUserStart = {
        mockTestId,
        remainTime,
        step,
      };
      await mockTestStart(payload);
      navigate(`/exam/mock-test/detail?id=${mockTestId}`);
    } catch (error) {
      notificationController.error({
        message: error,
      });
    }
  };

  return (
    <>
      {!result ? (
        <>
          <div className="flex flex-col rounded-lg bg-white p-8">
            <S.Title level={3}>{detailMockTest?.name}</S.Title>
            <S.Wrapper>
              <S.Column>
                <p>{t('page.mockTest.content')}</p>
                {currentMockTest && currentMockTest.status == MOCK_TEST_USER_STATUS.IN_PROGRESS ? (
                  <Button className="mt-4" type="primary" onClick={() => setIsModalResume(true)}>
                    {t('page.mockTest.button.continue')}
                  </Button>
                ) : (
                  <Button className="mt-4" type="primary" onClick={() => setIsModalStart(true)}>
                    {t('page.mockTest.button.start')}
                  </Button>
                )}
              </S.Column>
              <S.Column>
                <p>{t('page.mockTest.communicativeScores')}:</p>
                <S.Ul>
                  <li>{t('common.speaking')}</li>
                  <li>{t('common.writing')}</li>
                  <li>{t('common.reading')}</li>
                  <li>{t('common.listening')}</li>
                </S.Ul>
                <p>{t('page.mockTest.enablingScores')}:</p>
                <S.Ul>
                  <li>{t('page.mockTest.grammar')}</li>
                  <li>{t('page.mockTest.fluency')}</li>
                  <li>{t('page.mockTest.writtenDiscourse')}</li>
                  <li>{t('page.mockTest.pronunciation')}</li>
                  <li>{t('page.mockTest.vocabulary')}</li>
                  <li>{t('page.mockTest.spelling')}</li>
                </S.Ul>
              </S.Column>
            </S.Wrapper>
            <div>
              <p>{t(`page.mockTest.NOTE_${detailMockTest?.type}`)}</p>
            </div>
          </div>
          <div className="flex flex-col rounded-lg bg-white p-8 mt-4">
            <S.Title level={3}>{t('page.mockTest.history')}</S.Title>
            <ExamMockTestTable
              mockTestHis={data}
              onContinue={() => setIsModalResume(true)}
              onResultDone={handleResultClick}
              onDelete={handleModalDeleteClick}
              onResultFinished={handleResultFinish}
            />
          </div>
        </>
      ) : (
        <ExamMockTestResult mockTestUserId={mockTestUserId} />
      )}

      {/* modal confirm start  */}
      <Modal
        title={t('page.mockTest.modal.title_start')}
        visible={isModalStart}
        onOk={handleStartClick}
        onCancel={() => setIsModalStart(false)}
        cancelText={t('page.mockTest.modal.cancel')}
        okText={t('page.mockTest.button.start')}
      >
        <p>{currentMockTest?.name}</p>
        <p>{t('page.mockTest.modal.content_start')}</p>
      </Modal>

      {/* Modal confirm resune */}
      <Modal
        title={t('page.mockTest.modal.title_resume')}
        visible={isModalResume}
        onCancel={() => setIsModalResume(false)}
        footer={[
          <Button key="reset" onClick={handleReset}>
            {t('page.mockTest.modal.reset')}
          </Button>,
          <Button key="ok" type="primary" onClick={handleResumeClick}>
            {t('page.mockTest.modal.resume')}
          </Button>,
        ]}
      >
        <p>{currentMockTest?.name}</p>
        <p>{t('page.mockTest.modal.content_resume')}</p>
      </Modal>

      {/* Modal confirm delete */}
      <Modal
        title={t('page.mockTest.modal.title_delete')}
        visible={isModalDelete}
        onOk={handleDeleteClick}
        onCancel={() => setIsModalDelete(false)}
        okText={t('page.mockTest.modal.delete')}
        cancelText={t('page.mockTest.modal.cancel')}
      >
        <p>{currentMockTest?.name}</p>
        <p>{t('page.mockTest.modal.content_delete')}</p>
        <p>{t('page.mockTest.modal.content_delete1')}</p>
      </Modal>
    </>
  );
};
